import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogClose } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { InlineFormSchemaDialogComponent } from '@dialogs/inline-form-schema-dialog/inline-form-schema-dialog.component';
import { ConfiguratorGridDropdownOption } from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { ButtonComponent, ConfirmationDialogComponent, MdsSelectDropdownModule, TagComponent } from '@mds/ui';
import {
  EPDManagementResult,
  EPDManagementService,
  EPDMenuSection,
  EPDRowActionSection,
  getResumeFormSchema,
  InitializeEpdFormSchemaResponse,
  InitializePayload
} from '@services/epd-management.service';
import { ToastrService } from '@services/toaster.service';
import {
  catchError,
  EMPTY,
  finalize,
  interval,
  map,
  Observable,
  startWith,
  Subscription,
  switchMap,
  takeUntil,
  takeWhile
} from 'rxjs';
import { EpdFamilyStatus, EXTERNAL_VERIFIER_WARNING, PCR, Standard } from '../../utils/epd-dashboard.model';

export type EpdCardAction =
  | 'changeStatus'
  | 'initializeEpd'
  | 'publish'
  | 'resumeEpd'
  | 'retry'
  | 'submitForExternalVerification'
  | 'seeVerifierNotes'
  | 'openReviewFeedback'
  | 'startOver'
  | 'viewPublishedEpd';

interface StatusTagConfig {
  type: string;
  icon: MdsIcon;
}

interface FooterAction {
  label: string;
  icon: MdsIcon;
  type: 'primary' | 'secondary';
  event: EpdCardAction;
  iconRight?: boolean;
  disabled?: boolean;
}

const STATUS_TAG_MAP: Record<string, StatusTagConfig> = {
  'EPD not initiated': { type: 'off-soft', icon: MdsIcon.CANCELLED },
  'EPD Handout in progress': { type: 'warning-soft', icon: MdsIcon.PENDING },
  'EPD Handout failed': { type: 'error-soft', icon: MdsIcon.WARNING },
  'Sent for internal verification': { type: 'warning-soft', icon: MdsIcon.SEND },
  'Internal verification failed': { type: 'error-soft', icon: MdsIcon.ERROR_CIRCLE },
  'EPD Handout in progress (internal verification)': { type: 'warning-soft', icon: MdsIcon.PENDING },
  'EPD Handout failed (internal verification)': { type: 'error-soft', icon: MdsIcon.WARNING },
  'Internally verified': { type: 'success-soft', icon: MdsIcon.CHECKED },
  'Sent for external verification': { type: 'warning-soft', icon: MdsIcon.SEND },
  'External verification failed': { type: 'error-soft', icon: MdsIcon.ERROR_CIRCLE },
  'EPD Handout in progress (external verification)': { type: 'warning-soft', icon: MdsIcon.PENDING },
  'Externally verified': { type: 'success-soft', icon: MdsIcon.CHECKED },
  'EPD Handout failed (external verification)': { type: 'error-soft', icon: MdsIcon.WARNING },
  'Publishing in progress...': { type: 'warning-soft', icon: MdsIcon.PENDING },
  'Publishing failed': { type: 'error-soft', icon: MdsIcon.ERROR },
  Published: { type: 'success-hard', icon: MdsIcon.GLOBE }
};

const DEFAULT_STATUS_TAG: StatusTagConfig = { type: 'neutral-soft', icon: MdsIcon.PENDING };

const CONFIRM_ACTION_MAP: Partial<Record<EpdCardAction, { title: string; content: string }>> = {
  publish: { title: 'Publish EPD', content: 'Are you sure you want to publish this EPD?' },
  resumeEpd: { title: 'Resume EPD', content: 'Are you sure you want to resume this EPD?' },
  submitForExternalVerification: {
    title: 'Submit for external verification',
    content: 'Are you sure you want to submit this EPD for external verification?'
  },
  startOver: { title: 'Start over', content: 'Are you sure you want to reset this EPD?' }
};

const HANDOUT_IN_PROGRESS_HELPER_TEXT = 'System in progress. Please wait for completion to see actions.';

const HELPER_TEXT_MAP: Record<string, string> = {
  'EPD not initiated': 'Available once the EPD is initialized',
  'EPD Handout in progress': HANDOUT_IN_PROGRESS_HELPER_TEXT,
  'EPD Handout in progress (internal verification)': HANDOUT_IN_PROGRESS_HELPER_TEXT,
  'EPD Handout in progress (external verification)': HANDOUT_IN_PROGRESS_HELPER_TEXT,
  'Sent for internal verification': '', // handled dynamically with verifier name
  'Sent for external verification': 'Waiting for external approval.',
  'External verification failed': 'Submission rejected. See feedback for clarification',
  'Publishing in progress...': 'Publishing in progress. Please wait for completion.'
};

const STATUS_ACTION_MAP: Record<string, Pick<FooterAction, 'label' | 'icon'>> = {
  'EPD not initiated': { label: 'Start over', icon: MdsIcon.REPLAY },
  'Sent for internal verification': { label: 'Submit...', icon: MdsIcon.SEND },
  'Internal verification failed': { label: 'Reject...', icon: MdsIcon.CLOSE },
  'Internally verified': { label: 'Approve', icon: MdsIcon.CHECK_2 },
  'Sent for external verification': { label: 'Submit...', icon: MdsIcon.SEND },
  'External verification failed': { label: 'Reject...', icon: MdsIcon.CLOSE },
  'Externally verified': { label: 'Approve', icon: MdsIcon.CHECK_2 },
  Published: { label: 'Publish', icon: MdsIcon.UPLOAD }
};

const FOOTER_ACTIONS_MAP: Record<string, FooterAction[]> = {
  'EPD not initiated': [
    { label: 'Initialize EPD...', icon: MdsIcon.PLAY_CIRCLE, type: 'primary', event: 'initializeEpd' }
  ],
  'EPD Handout in progress': [],
  'EPD Handout failed': [{ label: 'Start over', icon: MdsIcon.REPLAY, type: 'primary', event: 'startOver' }],
  'Sent for internal verification': [
    { label: 'Start over', icon: MdsIcon.REPLAY, type: 'primary', event: 'startOver' }
  ],
  'Internal verification failed': [{ label: 'Resume EPD', icon: MdsIcon.REPLAY, type: 'primary', event: 'resumeEpd' }],
  'EPD Handout in progress (internal verification)': [],
  'EPD Handout failed (internal verification)': [
    { label: 'Resume EPD', icon: MdsIcon.REPLAY, type: 'primary', event: 'resumeEpd' }
  ],
  'Internally verified': [{ label: 'Publish', icon: MdsIcon.UPLOAD, type: 'primary', event: 'publish' }],
  'Sent for external verification': [
    { label: 'Start over', icon: MdsIcon.REPLAY, type: 'primary', event: 'startOver' }
  ],
  'External verification failed': [{ label: 'Resume EPD', icon: MdsIcon.REPLAY, type: 'primary', event: 'resumeEpd' }],
  'EPD Handout in progress (external verification)': [],
  'EPD Handout failed (external verification)': [
    { label: 'Start over', icon: MdsIcon.REPLAY, type: 'primary', event: 'startOver' }
  ],
  'Externally verified': [{ label: 'Publish', icon: MdsIcon.UPLOAD, type: 'primary', event: 'publish' }],
  'Publishing in progress...': [],
  'Publishing failed': [
    { label: 'Resume', icon: MdsIcon.REPLAY, type: 'primary', event: 'resumeEpd' },
    { label: 'Start over', icon: MdsIcon.REPLAY, type: 'secondary', event: 'startOver' }
  ],
  Published: [
    { label: 'Start over', icon: MdsIcon.REPLAY, type: 'secondary', event: 'startOver' },
    {
      label: 'View published EPD',
      icon: MdsIcon.OPEN_IN_NEW,
      type: 'primary',
      event: 'viewPublishedEpd',
      iconRight: true
    }
  ]
};

export type EpdCardWarningState = 'noFamily' | 'notReferent' | null;

@Component({
  selector: 'app-epd-management-card',
  standalone: true,
  imports: [
    ButtonComponent,
    TagComponent,
    MdsIconsModule,
    MdsSelectDropdownModule,
    DatePipe,
    NgTemplateOutlet,
    MatDialogClose
  ],
  templateUrl: './epd-management-card.component.html',
  styleUrls: ['./epd-management-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EpdManagementCardComponent {
  @Input() data: EpdFamilyStatus | null = null;
  @Input() statusFormSchema: EPDRowActionSection['formSchema'] = [];
  @Input() resumeFormSchema: EPDMenuSection['formSchema'] = [];
  @Input() hasFamily = true;
  @Input() productRid: string | null = null;
  @Input() familyRid: string | null = null;
  @Input() selectedStandard: Standard | null = null;
  @Input() selectedPcr: PCR | null = null;

  @ViewChild('noFamilyDialogBody', { static: true }) noFamilyDialogBody: TemplateRef<unknown>;
  @ViewChild('noFamilyDialogActions', { static: true }) noFamilyDialogActions: TemplateRef<unknown>;
  @ViewChild('verifierNotesDialogBody', { static: true }) verifierNotesDialogBody: TemplateRef<unknown>;
  @ViewChild('verifierNotesDialogActions', { static: true }) verifierNotesDialogActions: TemplateRef<unknown>;
  @ViewChild('confirmStatusDialogActions', { static: true }) confirmStatusDialogActions: TemplateRef<unknown>;

  protected readonly MdsIcon = MdsIcon;
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly epdManagementService = inject(EPDManagementService);
  private readonly toastrService = inject(ToastrService);
  private readonly router = inject(Router);
  private epdPollSub: Subscription | null = null;
  pendingStatus: string | null = null;
  isRefreshingExternalStatus = false;
  hasFetchedExternalStatus = false;

  get warningState(): EpdCardWarningState {
    if (!this.hasFamily) {
      return 'noFamily';
    }
    const currentProduct = this.data?.products?.find(
      (product) => product.rid.replace('#', '') === this.productRid?.replace('#', '')
    );
    if (currentProduct?.is_reference === false) {
      return 'notReferent';
    }
    return null;
  }

  get isInitiated(): boolean {
    return this.data?.status !== 'EPD not initiated';
  }

  get statusTag(): StatusTagConfig {
    return STATUS_TAG_MAP[this.data.status] ?? DEFAULT_STATUS_TAG;
  }

  get externalStatusTag(): StatusTagConfig {
    return STATUS_TAG_MAP[this.data.external_status] ?? DEFAULT_STATUS_TAG;
  }

  get externalStatusRefreshText(): string {
    if (this.isRefreshingExternalStatus) return 'Refreshing...';
    if (this.hasFetchedExternalStatus && !this.data.registration_number?.trim()) return 'Not Published';
    if (this.hasFetchedExternalStatus && this.data.last_synced) return this.data.last_synced;
    return 'Refresh Status';
  }

  get displayVersion(): string {
    return this.data.version === '00.00' ? '-' : this.data.version;
  }

  get selectedStandardName(): string {
    return (this.selectedStandard?.text ?? 'IBU').replace(/^EPD\s+/, '');
  }

  get externalEpdLinkLabel(): string {
    return `Link to EPD in ${this.selectedStandardName}`;
  }

  private static readonly VERIFIER_ROW_STATUSES = new Set([
    'Sent for internal verification',
    'Internal verification failed',
    'EPD Handout in progress (internal verification)',
    'EPD Handout failed (internal verification)',
    'Internally verified',
    'Sent for external verification',
    'External verification failed',
    'EPD Handout in progress (external verification)',
    'Externally verified',
    'EPD Handout failed (external verification)',
    'Published'
  ]);

  get showVerifierRow(): boolean {
    return EpdManagementCardComponent.VERIFIER_ROW_STATUSES.has(this.data?.status);
  }

  private static readonly EXTERNAL_VERIFIER_STATUSES = new Set([
    'External verification failed',
    'EPD Handout in progress (external verification)',
    'EPD Handout failed (external verification)',
    'Externally verified',
    'Sent for external verification',
    'Published'
  ]);

  get isExternalVerifierContext(): boolean {
    return EpdManagementCardComponent.EXTERNAL_VERIFIER_STATUSES.has(this.data?.status);
  }

  get statusOptions(): ConfiguratorGridDropdownOption[] {
    if (!this.data.row_actions?.changes_status || !this.data.row_actions?.statuses?.length) {
      return [];
    }
    return this.data.row_actions.statuses.map((status) => ({
      display_value: status,
      value: status,
      selected: status === (this.pendingStatus ?? this.data.status),
      default: status === this.data.status
    }));
  }

  get changeStatusMode(): 'dropdown' | 'helper' | 'disabled' {
    if (!this.isInitiated) {
      return 'disabled';
    }
    if (this.statusOptions.length) {
      return 'dropdown';
    }
    return 'helper';
  }

  get helperText(): string {
    const status = this.data.status;
    if (status === 'Sent for internal verification' && this.data.internal_verifier_name) {
      return `Waiting for response from internal verifier (${this.data.internal_verifier_name})`;
    }
    return HELPER_TEXT_MAP[status] ?? '';
  }

  get footerActions(): FooterAction[] {
    return (FOOTER_ACTIONS_MAP[this.data.status] ?? []).filter((action) => {
      if (action.event === 'startOver') {
        return true;
      }
      const PERMISSION_KEY_MAP: Partial<Record<EpdCardAction, string>> = {
        resumeEpd: 'resume_epd',
        openReviewFeedback: 'open_review_feedback'
      };
      const permissionKey = PERMISSION_KEY_MAP[action.event] ?? action.event;
      return this.data.menu_actions?.[permissionKey] !== false;
    });
  }

  get displayedFooterActions(): FooterAction[] {
    if (!this.pendingStatus) {
      return this.footerActions.map((action) => ({
        ...action,
        disabled: action.event === 'viewPublishedEpd' && !this.data.ibu_link
      }));
    }
    const statusAction = STATUS_ACTION_MAP[this.pendingStatus];
    return [
      {
        label: statusAction?.label ?? this.pendingStatus,
        icon: statusAction?.icon ?? MdsIcon.CHECK_2,
        type: 'primary',
        event: 'changeStatus'
      }
    ];
  }

  onStatusSelected(option: ConfiguratorGridDropdownOption | string): void {
    this.pendingStatus = typeof option === 'string' ? option : option.value;
  }

  onFooterAction(event: EpdCardAction): void {
    if (event === 'seeVerifierNotes' || event === 'openReviewFeedback') {
      this.openVerifierNotesDialog();
      return;
    }

    if (this.pendingStatus) {
      if (this.pendingStatus === 'Externally verified') {
        this.openStatusDateDialog(this.pendingStatus);
      } else {
        this.updateStatus(this.pendingStatus);
      }
      this.pendingStatus = null;
      return;
    }

    if (event === 'resumeEpd' && this.resumeFormSchema?.length) {
      this.openResumeDialog();
      return;
    }

    const confirmation = CONFIRM_ACTION_MAP[event];
    if (confirmation) {
      this.confirmAndEmit(event, confirmation.title, confirmation.content);
      return;
    }

    this.executeCardAction(event);
  }

  goToReferentProduct(): void {
    const referentProduct = this.data?.products?.find((product) => product.is_reference);
    if (!referentProduct) return;

    this.router.navigate([`/epd-dashboard/${referentProduct.rid.replace('#', '')}`], {
      queryParams: this.selectedStandard ? { method: this.selectedStandard.value } : undefined
    });
  }

  downloadHandout(): void {
    const endpoint = this.data?.latest_epd_handout_download_link;
    if (!endpoint) return;

    this.epdManagementService
      .fetchArtifactDownloadLink(endpoint)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.toastrService.showMessage('Download failed');
          return EMPTY;
        })
      )
      .subscribe(({ url }) => {
        if (url) {
          window.open(url, '_blank');
          return;
        }
        this.toastrService.showMessage('Download failed');
      });
  }

  private executeCardAction(event: EpdCardAction): void {
    const familyRid = this.data?.rid;
    if (!familyRid) return;

    switch (event) {
      case 'initializeEpd':
        this.initializeEpd();
        break;
      case 'publish':
        this.executeAction(
          () => this.epdManagementService.publish(familyRid),
          'EPD submitted for publishing',
          'Failed to publish EPD',
          true
        );
        break;
      case 'resumeEpd':
        this.executeAction(
          () => this.epdManagementService.resume(familyRid, 'No'),
          'EPD resume initiated',
          'Failed to resume EPD',
          true
        );
        break;
      case 'retry':
        this.executeAction(
          () => this.epdManagementService.resume(familyRid, 'No'),
          'EPD handout retry initiated',
          'Failed to retry EPD handout',
          true
        );
        break;
      case 'submitForExternalVerification':
        this.executeAction(
          () => this.epdManagementService.updateStatus(familyRid.replace('#', ''), 'Sent for external verification'),
          'Submitted for external verification',
          'Failed to submit for external verification'
        );
        break;
      case 'startOver':
        this.executeAction(
          () => this.epdManagementService.updateStatus(familyRid.replace('#', ''), 'EPD not initiated'),
          'EPD status reset',
          'Failed to reset EPD'
        );
        break;
      case 'viewPublishedEpd':
        if (this.data.ibu_link) window.open(this.data.ibu_link, '_blank');
        break;
    }
  }

  private openStatusDateDialog(status: string): void {
    if (!this.data?.rid) return;

    const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
      panelClass: 'inline-schema',
      data: {
        header: 'Update status',
        formSchema: this.statusFormSchema
      }
    });

    dialogRef.componentInstance.submitDataEmitter
      .pipe(takeUntil(dialogRef.afterClosed()), takeUntilDestroyed(this.destroyRef))
      .subscribe((form) => {
        if (!form.valid) return;
        const date = form.value?.Inputs?.externally_verified_date?.split(' ')[0];
        if (!date) return;

        dialogRef.close();
        this.updateStatus(status, date);
      });
  }

  private openResumeDialog(): void {
    const familyRid = this.data?.rid;
    if (!familyRid) return;

    const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
      panelClass: 'inline-schema',
      data: {
        header: 'Resume EPD',
        formSchema: this.resumeFormSchema
      }
    });

    dialogRef.componentInstance.submitDataEmitter
      .pipe(takeUntil(dialogRef.afterClosed()), takeUntilDestroyed(this.destroyRef))
      .subscribe((form) => {
        if (!form.valid) return;
        const regenerateModels = form.value?.Inputs?.lc_models_recalculation === 'Yes' ? 'Yes' : 'No';

        dialogRef.close();
        this.executeAction(
          () => this.epdManagementService.resume(familyRid, regenerateModels),
          'EPD resume initiated',
          'Failed to resume EPD',
          true
        );
      });
  }

  private applyStatusResponse(response: EPDManagementResult): void {
    this.statusFormSchema = response.formSchema?.row_actions?.status?.formSchema ?? this.statusFormSchema;
    this.resumeFormSchema = response.formSchema?.menus
      ? getResumeFormSchema(response.formSchema.menus)
      : this.resumeFormSchema;
    this.data = {
      ...this.data,
      ...response.data,
      version: `${String(response.data.version_major).padStart(2, '0')}.${String(response.data.version_minor).padStart(2, '0')}`
    };
    this.cdr.markForCheck();
  }

  private updateStatus(status: string, date?: string): void {
    if (!this.data?.rid) return;
    const familyRid = this.data.rid;
    const statusUpdate = date
      ? this.epdManagementService.updateStatus(familyRid.replace('#', ''), status, date)
      : this.epdManagementService.updateStatus(familyRid.replace('#', ''), status);
    statusUpdate
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.toastrService.showMessage('Failed to update EPD status');
          return EMPTY;
        })
      )
      .subscribe(() => this.refreshCard(familyRid));
  }

  private initializeEpd(): void {
    const familyRid = this.familyRid ?? this.data?.rid;
    const selectedStandard = this.selectedStandard?.value;
    const referenceProductRid = this.data?.reference_product || this.productRid;
    if (!familyRid || !selectedStandard || !referenceProductRid) return;

    this.fetchInitializeEpdFormSchema(familyRid, referenceProductRid, selectedStandard)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.toastrService.showMessage('Failed to load EPD initialization form');
          return EMPTY;
        })
      )
      .subscribe(({ fields: formSchema, warning }) => {
        const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
          panelClass: 'inline-schema',
          data: {
            header: 'Initialize EPD',
            formSchema,
            confirmText: 'Initialize',
            warning,
            lines: [
              {
                key: 'epd_standard',
                value: selectedStandard,
                display_value: this.selectedStandard?.text
              },
              { key: 'pcr', value: this.selectedPcr?.rid, display_value: this.selectedPcr?.name }
            ]
          }
        });
        dialogRef.componentInstance.submitDataEmitter
          .pipe(takeUntil(dialogRef.afterClosed()), takeUntilDestroyed(this.destroyRef))
          .subscribe((result) => {
            if (!result.valid) return;

            const payload = this.buildInitializePayload({
              epd_standard: selectedStandard,
              pcr: this.selectedPcr?.rid,
              ...result.updatedValues
            });
            this.epdManagementService
              .initializeEPD(familyRid, payload)
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError(() => {
                  this.toastrService.showMessage('Failed to initialize EPD');
                  return EMPTY;
                })
              )
              .subscribe((response) => {
                dialogRef.close();
                this.toastrService.showMessage(response.message);
                this.pollStatus(familyRid);
              });
          });
        this.cdr.markForCheck();
      });
  }

  private fetchInitializeEpdFormSchema(familyRid: string, referenceProductRid: string, selectedStandard: string) {
    return this.epdManagementService.getInitializeEpdFormSchema(familyRid).pipe(
      map((response) => ({
        ...this.buildInitializeEpdFormSchema(response, referenceProductRid, selectedStandard),
        warning: response.warning
      }))
    );
  }

  private buildInitializeEpdFormSchema(
    response: InitializeEpdFormSchemaResponse,
    referenceProductRid: string,
    selectedStandard: string
  ) {
    const fields = (response.formschemas[selectedStandard] || []).map((field) => {
      if (field.key !== 'external_verifier') return field;
      const verifiers = Array.isArray(field.enum) ? field.enum : [];
      return verifiers.length
        ? { ...field, enum: [...verifiers].sort((a, b) => a.name.localeCompare(b.name)) }
        : { ...field, enum: verifiers, question: EXTERNAL_VERIFIER_WARNING };
    });
    const selectedStandardField = {
      ...response.standards,
      key: 'epd_standard',
      defaultValue: selectedStandard,
      value: selectedStandard,
      formtype: 'disabled'
    };

    return {
      fields: [selectedStandardField, ...fields].map((fieldSchema) =>
        fieldSchema.key === 'pcr'
          ? {
              ...fieldSchema,
              options: (fieldSchema.options as string)?.replace(
                '<reference_product>',
                referenceProductRid.replace('#', '')
              ),
              default_options: fieldSchema.default_options?.replace(
                '<reference_product>',
                referenceProductRid.replace('#', '')
              ),
              formtype: 'disabled'
            }
          : fieldSchema
      )
    };
  }

  private buildInitializePayload(values: Record<string, any>): InitializePayload {
    const languages = Array.isArray(values.languages) ? values.languages : Object.values(values.languages ?? {});
    return {
      pcr_rid: values.pcr?.replace('#', ''),
      update_type: values.epd_version,
      languages,
      is_external: values.epd_usage === 'external' || !!values.external_verifier,
      epd_version: values.epd_version,
      epd_usage: values.epd_usage,
      regenerate_lc_models: values.lc_models_recalculation === 'Yes',
      internal_verifier: values.internal_verifier?.replace('#', ''),
      external_verifier: values.external_verifier,
      standard: values.epd_standard,
      comment: values.latest_epd_family_user_log
    };
  }

  private executeAction(
    action: () => Observable<unknown>,
    successMessage: string,
    errorMessage: string,
    poll = false
  ): void {
    action()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.toastrService.showMessage(errorMessage);
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.toastrService.showMessage(successMessage);
        const familyRid = this.data.rid;
        poll ? this.pollStatus(familyRid) : this.refreshCard(familyRid);
      });
  }

  private refreshCard(familyRid: string): void {
    this.epdManagementService
      .fetchEPDFamilyStatus(familyRid.replace('#', ''))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY)
      )
      .subscribe((response) => this.applyStatusResponse(response));
  }

  private pollStatus(familyRid: string): void {
    this.epdPollSub?.unsubscribe();
    this.epdPollSub = interval(30000)
      .pipe(
        startWith(0),
        switchMap(() => this.epdManagementService.fetchEPDFamilyStatus(familyRid.replace('#', ''))),
        takeWhile((response) => response.data.status.includes('progress'), true),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY)
      )
      .subscribe((response) => this.applyStatusResponse(response));
  }

  refreshExternalStatus(): void {
    if (this.isRefreshingExternalStatus || !this.data?.rid) return;
    this.isRefreshingExternalStatus = true;

    this.epdManagementService
      .fetchExternalStatus(this.data.rid.replace('#', ''))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.toastrService.showMessage('Failed to refresh external status');
          return EMPTY;
        }),
        finalize(() => {
          this.isRefreshingExternalStatus = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe((response) => {
        if (!response.data) return;
        this.hasFetchedExternalStatus = true;
        const ext = response.data;
        this.data = {
          ...this.data,
          external_status: ext.external_epd_status,
          dataset_version: ext.published_dataset_version,
          registration_number: ext.registration_number,
          last_revision: ext.date_of_last_revision,
          publication_date: ext.publication_date_of_epd,
          valid_until: ext.data_set_valid_until,
          last_synced: ext.last_synced_at,
          ibu_link: ext.ibu_data_url
        };
        this.cdr.markForCheck();
      });
  }

  private confirmAndEmit(event: EpdCardAction, title: string, content: string): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      data: { title, content, actions: this.confirmStatusDialogActions },
      width: '40vw'
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.executeCardAction(event);
        }
      });
  }

  openNoFamilyDialog(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'No family assigned',
        titleIcon: MdsIcon.INFO,
        content: '',
        body: this.noFamilyDialogBody,
        actions: this.noFamilyDialogActions
      },
      width: '480px'
    });
  }

  openVerifierNotesDialog(): void {
    if (!this.data?.latest_epd_family_user_log) return;
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Notes from verifier',
        titleIcon: MdsIcon.NOTES,
        content: '',
        body: this.verifierNotesDialogBody,
        actions: this.verifierNotesDialogActions
      },
      width: '480px'
    });
  }
}
